export function researchStepMap(boardId) {
    return {
        menu: null,
        menuX: 0,
        menuY: 0,
        stateOpen: false,
        dragging: null,
        landing: null,
        hiddenNodeIds: [],

        init() {
            try {
                this.hiddenNodeIds = JSON.parse(localStorage.getItem(this.storageKey()) ?? '[]');
            } catch (error) {
                this.hiddenNodeIds = [];
            }
        },

        storageKey() {
            return `zar-runbook-hidden:${boardId}`;
        },

        row(element) {
            return element?.closest?.('[data-map-entry]') ?? null;
        },

        itemFrom(element) {
            const row = this.row(element);
            if (!row) return null;

            return {
                entryId: Number(row.dataset.entryId ?? 0),
                nodeId: row.dataset.nodeId ?? '',
                title: row.dataset.title ?? 'Step',
                number: row.dataset.stepNumber ?? '',
                depth: Number(row.dataset.depth ?? 0),
                runbookStep: row.dataset.runbookStep === '1',
                state: row.dataset.state ?? 'backlog',
                reorderable: row.dataset.reorderable === '1',
            };
        },

        openMenu(event) {
            event.preventDefault();
            event.stopPropagation();
            const item = this.itemFrom(event.currentTarget);
            if (!item) return;

            const button = event.currentTarget.closest('button');
            const rect = button?.getBoundingClientRect();
            const x = event.clientX || rect?.right || 16;
            const y = event.clientY || rect?.bottom || 16;
            this.menuX = Math.max(8, Math.min(x, window.innerWidth - 232));
            this.menuY = Math.max(8, Math.min(y, window.innerHeight - 330));
            this.menu = item;
            this.stateOpen = false;
        },

        closeMenu() {
            this.menu = null;
            this.stateOpen = false;
        },

        isHidden(nodeId) {
            return this.hiddenNodeIds.includes(String(nodeId));
        },

        toggleVisibility() {
            if (!this.menu?.nodeId) return;
            const hidden = !this.isHidden(this.menu.nodeId);
            this.hiddenNodeIds = hidden
                ? [...this.hiddenNodeIds, this.menu.nodeId]
                : this.hiddenNodeIds.filter((id) => id !== this.menu.nodeId);
            try {
                localStorage.setItem(this.storageKey(), JSON.stringify(this.hiddenNodeIds));
            } catch (error) {
                // Private browsing may refuse persistence; the current session still works.
            }
            window.dispatchEvent(new CustomEvent('research-map-visibility', {
                detail: { nodeId: this.menu.nodeId, hidden },
            }));
            this.closeMenu();
        },

        openEntry() {
            if (!this.menu?.entryId) return;
            this.$wire.focusEntry(this.menu.entryId);
            this.closeMenu();
        },

        assign() {
            if (!this.menu?.entryId) return;
            this.$wire.openAssign(this.menu.entryId);
            this.closeMenu();
        },

        setState(state) {
            if (!this.menu?.entryId) return;
            this.$wire.setRunbookStepState(this.menu.entryId, state);
            this.closeMenu();
        },

        focusDependencies() {
            if (!this.menu?.entryId) return;
            this.$wire.focusMapDependencies(this.menu.entryId);
            this.closeMenu();
        },

        dragStart(event) {
            const item = this.itemFrom(event.currentTarget);
            if (!item?.reorderable) {
                event.preventDefault();
                return;
            }

            this.closeMenu();
            this.dragging = item;
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', String(item.entryId));
        },

        dragEnd() {
            this.dragging = null;
            this.landing = null;
        },

        canLand(element) {
            const target = this.itemFrom(element);
            return Boolean(this.dragging && target && target.reorderable
                && target.entryId !== this.dragging.entryId
                && target.depth === this.dragging.depth);
        },

        dragOver(event, placement) {
            if (!this.canLand(event.currentTarget)) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
            const target = this.itemFrom(event.currentTarget);
            this.landing = `${target.entryId}:${placement}`;
        },

        isLanding(element, placement) {
            const target = this.itemFrom(element);
            return target && this.landing === `${target.entryId}:${placement}`;
        },

        drop(event, placement) {
            if (!this.canLand(event.currentTarget)) return;
            event.preventDefault();
            const target = this.itemFrom(event.currentTarget);
            const source = this.dragging;
            this.dragEnd();
            this.$wire.reorderMapStep(source.entryId, target.entryId, placement);
        },
    };
}
